// frontend/src/context/PaymentContext.jsx - Fixed with all exports
import React, { createContext, useContext, useState, useCallback } from "react";
import {
  getPaymentMethods as apiGetPaymentMethods,
  addPaymentMethod as apiAddPaymentMethod,
  removePaymentMethod as apiRemovePaymentMethod,
  setDefaultPaymentMethod as apiSetDefaultPaymentMethod,
  updatePaymentMethod as apiUpdatePaymentMethod,
} from "../api";

// Re-export the API functions for direct use
export const getPaymentMethods = apiGetPaymentMethods;
export const addPaymentMethod = apiAddPaymentMethod;
export const removePaymentMethod = apiRemovePaymentMethod;
export const setDefaultPaymentMethod = apiSetDefaultPaymentMethod;
export const updatePaymentMethod = apiUpdatePaymentMethod;

const PaymentContext = createContext();

export const usePayment = () => {
  const context = useContext(PaymentContext);
  if (!context) {
    throw new Error("usePayment must be used within a PaymentProvider");
  }
  return context;
};

export const PaymentProvider = ({ children }) => {
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchPaymentMethods = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiGetPaymentMethods();
      const methods = response.data?.data || [];
      setPaymentMethods(methods);
      return methods;
    } catch (err) {
      console.error("Error fetching payment methods:", err);
      setError(err.response?.data?.message || "Failed to load payment methods");
      setPaymentMethods([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const addPayment = useCallback(
    async (paymentData) => {
      setLoading(true);
      setError(null);
      try {
        const response = await apiAddPaymentMethod(paymentData);
        await fetchPaymentMethods();
        return response.data;
      } catch (err) {
        console.error("Error adding payment method:", err);
        setError(err.response?.data?.message || "Failed to add payment method");
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [fetchPaymentMethods],
  );

  const removePayment = useCallback(
    async (paymentId) => {
      setLoading(true);
      setError(null);
      try {
        await apiRemovePaymentMethod(paymentId);
        await fetchPaymentMethods();
      } catch (err) {
        console.error("Error removing payment method:", err);
        setError(
          err.response?.data?.message || "Failed to remove payment method",
        );
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [fetchPaymentMethods],
  );

  const setDefault = useCallback(
    async (paymentId) => {
      setLoading(true);
      setError(null);
      try {
        await apiSetDefaultPaymentMethod(paymentId);
        await fetchPaymentMethods();
      } catch (err) {
        console.error("Error setting default payment method:", err);
        setError(
          err.response?.data?.message || "Failed to set default payment method",
        );
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [fetchPaymentMethods],
  );

  const updatePayment = useCallback(
    async (paymentId, data) => {
      setLoading(true);
      setError(null);
      try {
        const response = await apiUpdatePaymentMethod(paymentId, data);
        await fetchPaymentMethods();
        return response.data;
      } catch (err) {
        console.error("Error updating payment method:", err);
        setError(
          err.response?.data?.message || "Failed to update payment method",
        );
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [fetchPaymentMethods],
  );

  const getDefaultPaymentMethod = useCallback(() => {
    return paymentMethods.find((m) => m.isDefault) || paymentMethods[0] || null;
  }, [paymentMethods]);

  const getPaymentMethodById = useCallback(
    (id) => {
      return paymentMethods.find((m) => m._id === id) || null;
    },
    [paymentMethods],
  );

  const hasSavedPaymentMethods = useCallback(() => {
    return paymentMethods.length > 0;
  }, [paymentMethods]);

  const clearPaymentMethods = useCallback(() => {
    setPaymentMethods([]);
  }, []);

  const value = {
    paymentMethods,
    loading,
    error,
    fetchPaymentMethods,
    addPayment,
    removePayment,
    setDefault,
    updatePayment,
    getDefaultPaymentMethod,
    getPaymentMethodById,
    hasSavedPaymentMethods,
    clearPaymentMethods,
  };

  return (
    <PaymentContext.Provider value={value}>{children}</PaymentContext.Provider>
  );
};

export default PaymentProvider;